type Translate = (key: string) => string;

/**
 * Map a port-forward write/delete typed error code to an
 * operator-facing message. Unknown codes fall through to the BE's
 * `detail` (or the code itself if detail is empty) so we never silently
 * swallow a new error class.
 *
 * Spec: docs/superpowers/specs/2026-05-31-router-autodiscovery.md §C.
 * BE: services/plugins/equipment_discovery/api/router_writes.py
 */
export function translatePortForwardError(
  t: Translate,
  code: string,
  detail: string | null | undefined,
): string {
  const known = new Set([
    'unsupported_router_model',
    'credentials_rejected',
    'unreachable',
    'missing_field',
    'bad_protocol',
    'bad_external_port',
    'bad_internal_port',
    'bad_comment',
    'external_port_reserved',
    'device_rejected',
    'readback_missing',
    'readback_still_present',
    // 2026-06-02 Group A: typed errors from auto-allocate + connectivity-check
    // + designate-host endpoints. `endpoint_not_deployed` is FE-synthetic for
    // the staging window where Group A hasn't shipped yet.
    'no_reachable_path',
    'endpoint_not_deployed',
  ]);
  // `device_rejected` is special: unlike the other known codes (whose
  // i18n string fully describes the failure), its `detail` is the *actual*
  // NDM message the router returned, already humanised by the BE
  // (`_humanise_device_message`) into actionable text — e.g. "The router
  // has no LAN host with that MAC / IP yet. Wait for the device to appear…"
  // or "…firmware-compatibility issue — open a bug report." Show that
  // instead of swallowing it behind the generic "ответ CLI был непонятным";
  // fall back to the generic string only when the BE sent no detail.
  if (code === 'device_rejected') {
    return detail?.trim() || t('equipment.router.forwards.errors.device_rejected');
  }
  if (known.has(code)) {
    return t(`equipment.router.forwards.errors.${code}`);
  }
  return detail || code;
}
